import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  TrendingDown,
  Clock,
  ShoppingCart,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const BUSINESS_TYPES = [
  "Tienda/Retail",
  "Alimentos/Venta",
  "Electrodomésticos",
  "Ferretería",
];

const PROBLEMS = [
  "Cierres de caja lentos (+30 min)",
  "Diferencias en arqueo de caja",
  "Productos que 'desaparecen'",
  "Compras duplicadas (stock ciego)",
  "Ventas perdidas por falta de stock",
  "Descuentos mal aplicados",
  "Dificultad para ver ganancias",
  "Inventarios manuales eternos",
];

interface CalculatorState {
  businessType: string;
  monthlyRevenue: number;
  productCount: number;
  staffCount: number;
  selectedProblems: Set<number>;
  hourlyRate: number;
}

export default function LossCalculator() {
  const [step, setStep] = useState(1);
  const [state, setState] = useState<CalculatorState>({
    businessType: "",
    monthlyRevenue: 100000,
    productCount: 50,
    staffCount: 2,
    selectedProblems: new Set(),
    hourlyRate: 250,
  });

  const toggleProblem = (index: number) => {
    const newProblems = new Set(state.selectedProblems);
    if (newProblems.has(index)) {
      newProblems.delete(index);
    } else {
      newProblems.add(index);
    }
    setState({ ...state, selectedProblems: newProblems });
  };

  const calculations = useMemo(() => {
    const problemCount = state.selectedProblems.size;
    const hasStockoutProblem = state.selectedProblems.has(4);

    const inventoryLosses =
      state.monthlyRevenue * 0.04 * (problemCount / 8 || 0.1);
    const monthlyHours = (20 + 10 + 10) * (problemCount / 8 || 0.1);
    const timeLosses = monthlyHours * state.hourlyRate * 1.5;
    const lostSales = hasStockoutProblem ? state.monthlyRevenue * 0.12 : 0;
    const humanErrors = inventoryLosses * 0.2;

    const monthlyTotal = inventoryLosses + timeLosses + lostSales + humanErrors;
    const annualTotal = monthlyTotal * 12;
    const potentialSavings =
      (inventoryLosses * 0.85 + timeLosses * 0.9 + lostSales * 0.8) * 12;

    // Determine recommended plan based on parameters
    let recommendedPlanPrice = 1500; // Basic plan
    let recommendedPlanName = "Básico";

    if (
      state.productCount > 15 ||
      state.staffCount > 2 ||
      state.monthlyRevenue > 500000
    ) {
      recommendedPlanPrice = 3500; // Professional plan
      recommendedPlanName = "Profesional";
    }

    if (
      state.productCount > 50 ||
      state.staffCount > 10 ||
      state.monthlyRevenue > 2000000
    ) {
      recommendedPlanPrice = 6500; // Enterprise plan
      recommendedPlanName = "Enterprise";
    }

    return {
      inventoryLosses,
      timeLosses,
      lostSales,
      humanErrors,
      monthlyTotal,
      annualTotal,
      potentialSavings,
      monthlyHours,
      recommendedPlanPrice,
      recommendedPlanName,
    };
  }, [state]);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("es-CU", {
      style: "currency",
      currency: "CUP",
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <section id="calculator" className="py-20 relative overflow-hidden">
      {/* Abstract background shapes */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 pointer-events-none opacity-40">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[100px]" />
      </div>

      <div className="container px-4 mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-bold mb-4"
          >
            <Sparkles className="w-3 h-3" />
            DIAGNÓSTICO EMPRESARIAL
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display tracking-tight">
            Detecta las fugas de tu capital
          </h2>
          <p className="text-muted-foreground text-lg font-light leading-relaxed">
            Nuestra auditoría inteligente estima el impacto de la ineficiencia
            en tu flujo de caja real.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid lg:grid-cols-5 gap-10"
              >
                <div className="lg:col-span-3 space-y-8">
                  <div className="space-y-6">
                    <Label className="text-xl font-bold font-display">
                      Tipo de Operación
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {BUSINESS_TYPES.map((type) => (
                        <button
                          key={type}
                          onClick={() =>
                            setState({ ...state, businessType: type })
                          }
                          className={`p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 text-left relative overflow-hidden group ${
                            state.businessType === type
                              ? "border-primary bg-primary/5 ring-4 ring-primary/5"
                              : "border-slate-100 hover:border-slate-300 bg-white"
                          }`}
                        >
                          <span
                            className={`text-sm font-bold ${state.businessType === type ? "text-primary" : "text-slate-600"}`}
                          >
                            {type}
                          </span>
                          {state.businessType === type && (
                            <motion.div
                              layoutId="active"
                              className="absolute top-2 right-2 text-primary"
                            >
                              <CheckCircle2 className="w-5 h-5" />
                            </motion.div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-6 p-5 sm:p-8 bg-white rounded-2xl sm:rounded-[2rem] border border-slate-100 shadow-sm">
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3">
                        <Label className="text-sm font-bold text-slate-500 uppercase tracking-widest">
                          Facturación Mensual (CUP)
                        </Label>
                        <Input
                          type="number"
                          value={state.monthlyRevenue}
                          onChange={(e) =>
                            setState({
                              ...state,
                              monthlyRevenue: Number(e.target.value),
                            })
                          }
                          className="w-full sm:w-48 h-12 text-xl sm:text-2xl font-black text-primary font-display text-right border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 focus:bg-white dark:focus:bg-slate-900 dark:text-white rounded-xl"
                        />
                      </div>
                      <Slider
                        min={20000}
                        max={100000000}
                        step={10000}
                        value={[state.monthlyRevenue]}
                        onValueChange={(vals) =>
                          setState({ ...state, monthlyRevenue: vals[0] })
                        }
                        className="py-4"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
                      <div className="space-y-4">
                        <Label className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                          Productos
                        </Label>
                        <Input
                          type="number"
                          value={state.productCount}
                          onChange={(e) =>
                            setState({
                              ...state,
                              productCount: Number(e.target.value),
                            })
                          }
                          className="h-14 text-xl font-bold rounded-xl border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 focus:bg-white dark:focus:bg-slate-900 dark:text-white"
                        />
                      </div>
                      <div className="space-y-4">
                        <Label className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                          Personal
                        </Label>
                        <Input
                          type="number"
                          value={state.staffCount}
                          onChange={(e) =>
                            setState({
                              ...state,
                              staffCount: Number(e.target.value),
                            })
                          }
                          className="h-14 text-xl font-bold rounded-xl border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 focus:bg-white dark:focus:bg-slate-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2 flex flex-col justify-center">
                  <div className="glass-card p-10 rounded-[2.5rem] border-primary/10 dark:bg-slate-800/80 dark:border-slate-700">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-8 text-primary">
                      <Clock className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 font-display dark:text-white">
                      ¿Por qué estos datos?
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 font-light mb-8 leading-relaxed">
                      La escala de tu inventario y el tamaño de tu equipo son
                      los principales vectores de riesgo en la gestión manual.
                    </p>
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!state.businessType}
                      className="w-full h-16 rounded-full text-lg font-bold shadow-xl shadow-primary/20"
                    >
                      Continuar Diagnóstico
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-3xl mx-auto"
              >
                <div className="bg-white rounded-[2.5rem] p-10 shadow-xl border border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10">
                    {PROBLEMS.map((problem, index) => (
                      <button
                        key={index}
                        onClick={() => toggleProblem(index)}
                        className={`flex items-center gap-3 p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all text-left group ${
                          state.selectedProblems.has(index)
                            ? "border-orange-200 bg-orange-50/50 shadow-inner"
                            : "border-slate-50 hover:border-slate-200 bg-white"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 sm:w-6 sm:h-6 rounded-lg border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                            state.selectedProblems.has(index)
                              ? "bg-orange-500 border-orange-500 text-white"
                              : "border-slate-200 bg-white"
                          }`}
                        >
                          {state.selectedProblems.has(index) && (
                            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </div>
                        <span
                          className={`text-xs sm:text-sm font-bold ${state.selectedProblems.has(index) ? "text-orange-900" : "text-slate-600"}`}
                        >
                          {problem}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setStep(1)}
                      className="h-12 sm:h-16 px-6 sm:px-8 rounded-full font-bold order-2 sm:order-1"
                    >
                      Atrás
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      className="flex-1 h-12 sm:h-16 rounded-full text-base sm:text-lg font-bold shadow-xl shadow-primary/20 bg-primary order-1 sm:order-2"
                    >
                      Generar Reporte de Impacto
                      <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10"
              >
                <div className="space-y-6">
                  <div className="bg-slate-900 text-white rounded-2xl sm:rounded-[2.5rem] p-6 sm:p-10 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-[80px] -mr-32 -mt-32" />
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center text-red-400">
                          <AlertTriangle className="w-6 h-6" />
                        </div>
                        <span className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                          Capital en Riesgo
                        </span>
                      </div>

                      <div className="space-y-2 mb-10">
                        <p className="text-sm font-medium text-slate-400 uppercase tracking-widest">
                          Pérdida Mensual Estimada
                        </p>
                        <h4 className="text-4xl sm:text-6xl font-black font-display text-red-500">
                          {formatCurrency(calculations.monthlyTotal)}
                        </h4>
                      </div>

                      <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur">
                        <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">
                          Impacto Proyectado a 12 Meses
                        </p>
                        <p className="text-3xl font-bold text-white">
                          {formatCurrency(calculations.annualTotal)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    <BreakdownItem
                      icon="📦"
                      title="Inventario Ciego"
                      value={formatCurrency(calculations.inventoryLosses)}
                      color="amber"
                    />
                    <BreakdownItem
                      icon="⏰"
                      title="Fuga Operativa"
                      value={formatCurrency(calculations.timeLosses)}
                      color="cyan"
                    />
                    {calculations.lostSales > 0 && (
                      <BreakdownItem
                        icon="💸"
                        title="Ventas Fallidas"
                        value={formatCurrency(calculations.lostSales)}
                        color="purple"
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col justify-between p-6 sm:p-10 rounded-2xl sm:rounded-[2.5rem] border-2 border-primary/20 bg-white shadow-2xl">
                  <div className="space-y-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary text-xs font-black">
                      PROPUESTA DE RECUPERACIÓN
                    </div>

                    <h3 className="text-4xl font-black font-display leading-tight">
                      Recupera tu <br />{" "}
                      <span className="text-secondary">rentabilidad</span>
                    </h3>

                    <div className="space-y-4">
                      <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                          Ahorro Mensual con QvaPOS
                        </p>
                        <p className="text-4xl font-black text-slate-900">
                          {formatCurrency(calculations.potentialSavings / 12)}
                        </p>
                      </div>

                      <div className="flex items-center gap-6 p-6 rounded-2xl border-2 border-dashed border-slate-200">
                        <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            Inversión POS
                          </p>
                          <p className="text-xl font-bold text-slate-900">
                            {formatCurrency(calculations.recommendedPlanPrice)}
                            /mes
                          </p>
                          <p className="text-[10px] text-slate-500 font-medium mt-1">
                            Plan {calculations.recommendedPlanName}
                          </p>
                        </div>
                        <div className="w-px h-10 bg-slate-200" />
                        <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            ROI Proyectado
                          </p>
                          <p className="text-xl font-bold text-secondary">
                            {Math.max(
                              1,
                              Math.round(
                                (calculations.recommendedPlanPrice /
                                  (calculations.potentialSavings / 12)) *
                                  30,
                              ),
                            )}
                            -
                            {Math.max(
                              3,
                              Math.round(
                                (calculations.recommendedPlanPrice /
                                  (calculations.potentialSavings / 12)) *
                                  30,
                              ) + 5,
                            )}{" "}
                            Días
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12 space-y-4">
                    <Button
                      onClick={() =>
                        (window.location.href =
                          "http://127.0.0.1:5000/register")
                      }
                      className="w-full h-14 sm:h-20 rounded-full text-base sm:text-xl font-black bg-primary shadow-2xl shadow-primary/30"
                    >
                      RECUPERAR MI CAPITAL
                      <Sparkles className="ml-2 sm:ml-3 w-4 h-4 sm:w-6 sm:h-6" />
                    </Button>
                    <p className="text-center text-sm font-medium text-slate-400">
                      Digitaliza tu operación hoy y toma el control total de tu
                      inventario.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function BreakdownItem({
  icon,
  title,
  value,
  color,
}: {
  icon: string;
  title: string;
  value: string;
  color: string;
}) {
  const colors: Record<string, string> = {
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };

  return (
    <div
      className={`flex items-center justify-between p-5 rounded-2xl border-2 ${colors[color]}`}
    >
      <div className="flex items-center gap-4">
        <span className="text-2xl">{icon}</span>
        <span className="text-sm font-bold uppercase tracking-widest">
          {title}
        </span>
      </div>
      <span className="text-lg font-black">{value}</span>
    </div>
  );
}
